/*
 * BuzzTracker self-service onboarding — mock data + pure helpers.
 *
 * Ported from the Claude Design handoff (onboarding/ob-app.jsx) and adapted to
 * the Vue app. The "Find your business" step is wired to the real research API
 * (POST /api/onboard/research → mapResearchResult); the mock dataset below
 * still backs the "Preview states" menu and the simulated payment step.
 */

export const SOURCE_NAMES = {
  google: "Google",
  hellopeter: "Hellopeter",
  tripadvisor: "TripAdvisor",
  facebook: "Facebook",
  fresha: "Fresha",
  trustpilot: "Trustpilot",
};

export const SOURCE_KEYS = Object.keys(SOURCE_NAMES);

export const MOCK_BIZ = {
  name: "Beanstalk Coffee Co.",
  initial: "B",
  website: "beanstalkcoffee.co.za",
  industry: "Coffee & Café · Food & Beverage",
  desc: "A specialty coffee roaster and café group with three locations across Cape Town.",
};

export const mockChannels = () => [
  {
    id: "google",
    src: "google",
    url: "google.com/maps?cid=beanstalk",
    on: true,
    multi: true,
    branches: [
      {
        id: "g1",
        name: "Sea Point",
        addr: "Regent Rd, Sea Point",
        rating: 4.5,
        reviews: 612,
        on: true,
      },
      {
        id: "g2",
        name: "Claremont",
        addr: "Main Rd, Claremont",
        rating: 4.2,
        reviews: 388,
        on: true,
      },
      {
        id: "g3",
        name: "V&A Waterfront",
        addr: "Dock Rd, V&A",
        rating: 4.6,
        reviews: 921,
        on: true,
      },
    ],
  },
  {
    id: "hellopeter",
    src: "hellopeter",
    url: "hellopeter.com/beanstalk-coffee",
    on: true,
    rating: 3.8,
    reviews: 142,
  },
  {
    id: "facebook",
    src: "facebook",
    url: "facebook.com/beanstalkcoffee",
    on: true,
    rating: 4.4,
    reviews: 230,
  },
  {
    id: "tripadvisor",
    src: "tripadvisor",
    url: "tripadvisor.com/Beanstalk_Coffee",
    on: true,
    rating: 4.5,
    reviews: 88,
  },
  {
    id: "trustpilot",
    src: "trustpilot",
    url: "trustpilot.com/review/beanstalkcoffee.co.za",
    on: true,
    rating: 4.1,
    reviews: 54,
  },
];

export const mockCompetitors = () => [
  { name: "Truth Coffee Roasting", rating: 4.6, on: true },
  { name: "Bootlegger Coffee", rating: 4.3, on: true },
  { name: "Origin Coffee Roasting", rating: 4.5, on: true },
  { name: "Seattle Coffee Co.", rating: 4.0, on: false },
];

export const mockProducts = () =>
  [
    "Specialty coffee",
    "Cold brew",
    "Pastries & baking",
    "Retail coffee beans",
    "Catering",
    "Wholesale supply",
  ].map((name) => ({ name, on: true }));

export const PLANS = [
  {
    id: "starter",
    name: "Starter",
    priceMonthly: 399,
    priceYearly: 3990,
    locations: 1,
    reviewsPerMonth: 1500,
    benchmarks: 1,
    desc: "For a single site getting started.",
    feats: ["1 location", "1,500 reviews / month", "1 competitor benchmark"],
  },
  {
    id: "growth",
    name: "Growth",
    priceMonthly: 1199,
    priceYearly: 11990,
    badge: "Most popular",
    locations: 5,
    reviewsPerMonth: 8000,
    benchmarks: 3,
    desc: "For growing multi-location brands.",
    feats: [
      "Up to 5 locations",
      "8,000 reviews / month",
      "3 competitor benchmarks",
    ],
  },
  {
    id: "agency",
    name: "Agency",
    priceMonthly: 2999,
    priceYearly: 29990,
    locations: 25,
    reviewsPerMonth: 40000,
    benchmarks: 10,
    desc: "For agencies and large groups.",
    feats: [
      "Up to 25 locations",
      "40,000 reviews / month",
      "10 competitor benchmarks",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    priceMonthly: null,
    priceYearly: null,
    contact: true,
    locations: Infinity,
    reviewsPerMonth: 100000,
    benchmarks: Infinity,
    desc: "For large groups needing scale & support.",
    feats: [
      "Unlimited locations",
      "100,000+ reviews / month",
      "Custom benchmarks",
    ],
  },
];

export const RESEARCH_TASKS = [
  "Finding website & company details",
  "Scanning Google, Hellopeter, Facebook & more",
  "Detecting your products & services",
  "Identifying local competitors",
  "Compiling your workspace",
];

// Task tickers for the two research phases (company lookup, then channels).
export const COMPANY_TASKS = [
  "Finding your website",
  "Reading your company details",
  "Matching your industry",
];

export const CHANNEL_TASKS = [
  "Scanning Google, Hellopeter, Facebook & more",
  "Detecting your products & services",
  "Identifying local competitors",
];

// Cycling status copy under the hive-scan comb. One entry per honeycomb cell the
// bee visits (6), shared by both research phases — only the heading differs.
export const HIVE_TASKS = [
  "Finding your website",
  "Identifying your locations",
  "Matching your review profiles",
  "Scanning your competitors",
  "Detecting products & services",
  "Piecing it all together",
];

export const STEP_LABELS = ["Account", "Business", "Channels", "Plan", "Trial"];

/**
 * Count the selected channels. A multi-location channel (Google) counts its
 * ticked branches; a single channel counts as one when on.
 */
export function countSelected(channels) {
  return channels.reduce((n, c) => {
    if (c.multi) return n + (c.on ? c.branches.filter((b) => b.on).length : 0);
    return n + (c.on ? 1 : 0);
  }, 0);
}

/** Price helpers for the order summary (ZAR, tax-inclusive advertised price). */
export function priceBreakdown(plan, period = "monthly") {
  if (plan.contact) return { base: "Custom", total: "Custom" };
  const base = period === "yearly" ? plan.priceYearly : plan.priceMonthly;
  return { base, total: "R" + base };
}

export const TRIAL_DAYS = 14;

/** The date the free trial ends (today + TRIAL_DAYS), formatted for ZA. */
export function trialEndDate(days = TRIAL_DAYS) {
  const end = new Date(Date.now() + days * 864e5);
  return end.toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Recurring plan price, formatted (e.g. "R1,199"). This is the advertised
 * price the customer pays once the trial ends — R0 is due today.
 */
export function planPrice(plan, period = "monthly") {
  if (!plan || plan.contact) return "Custom";
  const base = period === "yearly" ? plan.priceYearly : plan.priceMonthly;
  return "R" + base.toLocaleString("en-ZA");
}

/** Count selected business locations (branches of multi-location channels). */
export function countLocations(channels) {
  return channels.reduce((n, c) => {
    if (c.multi) return n + (c.on ? c.branches.filter((b) => b.on).length : 0);
    return n;
  }, 0);
}

// ── Physical-site resolution ────────────────────────────────────────────────
// The same branches usually surface as several listings (e.g. six Google + six
// Fresha listings for the same six shops). Counting listings would double-count
// and inflate the plan tier, so we fold listings down to distinct physical
// sites by comparing their locality keys — a JS mirror of the backend's
// app/branch_matching.py, kept lightweight so the count stays reactive as the
// user ticks branches on and off.
const GENERIC_LOCATION_WORDS = new Set([
  "nails",
  "nail",
  "beauty",
  "lounge",
  "salon",
  "spa",
  "shop",
  "store",
  "branch",
  "the",
  "and",
]);
const SITE_MATCH_THRESHOLD = 0.82;

function tokenizeName(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean);
}

function localityKey(name, brandTokens) {
  return tokenizeName(name)
    .filter((t) => !brandTokens.has(t) && !GENERIC_LOCATION_WORDS.has(t))
    .join(" ");
}

function keySimilarity(a, b) {
  if (a === b) return 1;
  const ta = new Set(a.split(" ").filter(Boolean));
  const tb = new Set(b.split(" ").filter(Boolean));
  if (!ta.size || !tb.size) return 0;
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter += 1;
  const union = new Set([...ta, ...tb]).size;
  return Math.max(inter / union, (inter / Math.min(ta.size, tb.size)) * 0.95);
}

/**
 * Count distinct physical sites across the selected, location-bearing channels
 * (Google, Fresha …), de-duplicating listings that resolve to the same branch.
 * This is the number that should drive plan sizing, not the raw listing count.
 */
export function countPhysicalLocations(channels, companyName = "") {
  const brand = new Set(tokenizeName(companyName));
  const keys = [];
  for (const c of channels || []) {
    if (!c.on || !c.multi) continue;
    for (const b of c.branches || []) {
      if (b.on) keys.push(localityKey(b.name || "", brand));
    }
  }
  const clusters = [];
  for (const key of keys) {
    if (!clusters.some((k) => keySimilarity(key, k) >= SITE_MATCH_THRESHOLD)) {
      clusters.push(key);
    }
  }
  return clusters.length;
}

/**
 * The smallest plan whose location + benchmark caps both cover the detected
 * usage. PLANS is ordered smallest→largest, so the first fit wins; if nothing
 * fits (huge estate) we fall back to the largest (Enterprise).
 */
export function smallestFittingPlan(locations, competitors) {
  const fit = PLANS.find(
    (p) => locations <= p.locations && competitors <= p.benchmarks,
  );
  return (fit || PLANS[PLANS.length - 1]).id;
}

/** Count selected competitors (for benchmark-limit gating). */
export function countCompetitors(competitors) {
  return (competitors || []).filter((c) => c.on).length;
}

export function findPlan(id) {
  return PLANS.find((p) => p.id === id);
}

/**
 * Map the backend research result (POST /api/onboard/research) into the shapes
 * the wizard's step components expect. Research has no per-channel ratings /
 * review counts, so those fields are simply omitted (the UI renders them
 * conditionally).
 *
 * Returns { biz, channels, competitors, products, hasChannels, ok }.
 *   ok          — research found a usable business (name + website or channels)
 *   hasChannels — at least one review profile was discovered
 */
/**
 * Map the company-profile result (POST /api/onboard/research/company) into the
 * `biz` shape the wizard's confirm step expects.
 *
 * Returns { biz, ok }. `ok` means research identified a usable company (it has
 * a name) — the user still gets to confirm or reject it.
 */
export function mapCompanyResult(result) {
  const company = result?.company || {};
  const name = (company.name || "").trim() || "Your business";
  const industryRef =
    company.industry && typeof company.industry === "object"
      ? company.industry
      : null;
  const industryName = industryRef ? industryRef.name : company.industry;
  const countries = Array.isArray(company.countries)
    ? company.countries.filter(Boolean)
    : [];

  // Backend grounds the profile against Google Places (the same source as
  // channel discovery). "high" means a real business matched; anything else is
  // an unconfirmed match the wizard must show as a question, not a fact.
  const verified = company.confidence === "high";

  const biz = {
    name,
    initial: name.charAt(0).toUpperCase() || "•",
    website: company.website || "",
    industry: industryName || "",
    // Structured industry ({ slug, name, is_new }) preserved for the create call.
    industryRef,
    headquarters: company.headquarters || "",
    countries,
    desc: company.description || "",
    verified,
  };

  const ok = Boolean((company.name || "").trim());
  return { biz, ok };
}

/**
 * Map the channels result (POST /api/onboard/research/channels) into the
 * channels / competitors / products shapes the confirm-channels step expects.
 * Research has no per-channel ratings / review counts, so those fields are
 * simply omitted (the UI renders them conditionally).
 *
 * Returns { channels, competitors, products, hasChannels }.
 */
export function mapChannelsResult(result) {
  // Group entities by source. Sources that can return several locations (Google
  // Places, Fresha venues) collapse into a single multi-location channel with
  // per-branch toggles; single-result sources stay flat.
  const entities = (result?.entities || []).filter(
    (e) => e.url || e.platform_id,
  );
  const bySource = {};
  for (const e of entities) {
    const key = (e.source || "").toLowerCase();
    if (!SOURCE_NAMES[key]) continue;
    (bySource[key] ||= []).push(e);
  }

  const channels = [];
  for (const key of SOURCE_KEYS) {
    const list = bySource[key];
    if (!list || !list.length) continue;
    if (list.length > 1) {
      channels.push({
        id: key,
        src: key,
        url: list[0].url || "",
        on: true,
        multi: true,
        branches: list.map((e, i) => ({
          id: e.platform_id || key + i,
          name: e.name || "Location " + (i + 1),
          addr: "",
          // platform_id is what the backend persists as the channel identity.
          platformId: e.platform_id || e.url || "",
          on: true,
        })),
      });
    } else {
      const e = list[0];
      channels.push({
        id: key,
        src: key,
        url: e.url || "",
        platformId: e.platform_id || e.url || "",
        on: true,
      });
    }
  }

  const competitors = (result?.competitors || [])
    .filter((c) => c && c.name)
    .map((c) => ({ name: c.name, website: c.website || "", on: true }));

  const products = (result?.products_services || [])
    .filter((p) => p && p.name)
    .map((p) => ({ name: p.name, on: true }));

  const socialLinks = (result?.social_media || [])
    .filter((s) => s && s.url)
    .map((s) => ({ platform: s.platform || "", url: s.url }));

  const hasChannels = countSelected(channels) > 0;
  return { channels, competitors, products, socialLinks, hasChannels };
}

/**
 * Map a full research result (POST /api/onboard/research) — company profile and
 * channels together. Retained for callers that do the lookup in one shot.
 *
 * Returns { biz, channels, competitors, products, hasChannels, ok }.
 *   ok          — research found a usable business (name + website or channels)
 *   hasChannels — at least one review profile was discovered
 */
export function mapResearchResult(result) {
  const { biz } = mapCompanyResult(result);
  const { channels, competitors, products, hasChannels } =
    mapChannelsResult(result);
  const company = result?.company || {};
  const ok = Boolean(company.name && (company.website || hasChannels));
  return { biz, channels, competitors, products, hasChannels, ok };
}

/**
 * Turn the wizard's selected channels into create-payload entities. One entity
 * per ticked branch for multi-location sources (carrying the branch name so each
 * location keeps its real name); single channels become one entity. Entities
 * without a platform identity are dropped so the backend's validation passes.
 *
 * Shared by both the self-service wizard (buildCreatePayload) and the admin
 * onboarding view so entity construction stays identical across flows.
 */
export function channelsToEntities(channels) {
  const entities = [];
  for (const c of channels || []) {
    if (!c.on) continue;
    const source = c.src.toUpperCase();
    if (c.multi) {
      for (const b of c.branches || []) {
        if (!b.on) continue;
        const platformId = (b.platformId || "").trim();
        if (platformId)
          entities.push({
            source,
            platform_id: platformId,
            name: (b.name || "").trim(),
          });
      }
    } else {
      const platformId = (c.platformId || c.url || "").trim();
      if (platformId) entities.push({ source, platform_id: platformId });
    }
  }
  return entities;
}

/**
 * Build the POST /api/onboard/create payload from the wizard's collected state.
 * Selected channels become entities (one per ticked branch for multi-location
 * sources); entities without a platform identity are dropped so the backend's
 * validation passes. Industry is sent as a structured slug/name reference.
 */
export function buildCreatePayload({
  biz,
  channels,
  competitors,
  email,
  password,
}) {
  const entities = channelsToEntities(channels);

  const ref = biz.industryRef;
  let industry;
  if (ref && ref.slug) {
    industry = {
      slug: ref.slug,
      name: ref.name || "",
      is_new: Boolean(ref.is_new),
    };
  } else if (ref && ref.name) {
    industry = { name: ref.name, is_new: true };
  } else if (biz.industry) {
    industry = { name: biz.industry, is_new: true };
  } else {
    industry = { name: "Other", is_new: true };
  }

  const selectedCompetitors = (competitors || [])
    .filter((c) => c.on && c.name)
    .map((c) => ({ name: c.name, website: c.website || "" }));

  return {
    organisation_name: biz.name,
    admin_email: (email || "").trim(),
    admin_password: password || "",
    company: {
      name: biz.name,
      website: biz.website || "",
      description: biz.desc || "",
      headquarters: biz.headquarters || "",
      countries: Array.isArray(biz.countries) ? biz.countries : [],
      social_links: Array.isArray(biz.socialLinks) ? biz.socialLinks : [],
      industry,
    },
    entities,
    competitors: selectedCompetitors,
  };
}
